import { PredictionLabel } from '@modules/common/components/PredictionLabel';
import { PredictionLabelProps } from '@modules/common/components/PredictionLabel/PredictionLabel';
import { SelectablePredictionLabelMenu } from '@modules/common/components/SelectablePredictionLabel/SelectablePredictionLabelMenu';
import { Category } from '@modules/common/types';
import { useFeedbackOnFeedbackStore } from '@modules/feedback/store';
import { FeedbackOnFeedbackActionType } from '@modules/feedback/store/feedbackOnFeedbackActionType';
import { MergeSuggestionDetailsDrawer } from '@modules/taxonomy/components/MergeSuggestionDetailsDrawer';
import { EditReasonDrawer } from '@modules/taxonomy/components/Reason/EditReasonDrawer';
import { TrackedKeywordsEditDrawer } from '@modules/taxonomy/components/TrackedKeywords/TrackedKeywordsEditDrawer';
import { useActiveMergeSuggestionsState } from '@modules/taxonomy/hooks';
import { MenuItem, Popper, Stack } from '@mui/material';
import { LabelType, ManipulationType } from 'generated/graphql';
import { useCallback, useRef, useState } from 'react';
import { Feedback, FeedbackLabelType } from 'v2/core/entities/feedback';

interface SelectablePredictionLabelProps extends PredictionLabelProps {
  disabled?: boolean;
  category: Category;
  onClick: () => void;
  feedback: Feedback;
  isEditable: boolean;
  isClickable: boolean;
  isMenuOpen: boolean;
  setIsMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  shouldHideVisibilityIcon?: boolean;
}

export const SelectablePredictionLabel = ({
  label,
  category,
  feedback,
  isSelected,
  size,
  disabled,
  onClick,
  isEditable,
  isClickable,
  isMenuOpen,
  setIsMenuOpen,
  shouldHideVisibilityIcon,
}: SelectablePredictionLabelProps) => {
  const popperRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isEditCategoryMode, setIsEditCategoryMode] = useState(false);

  const [isEditPredictionDrawerOpen, setIsEditPredictionDrawerOpen] =
    useState(false);

  const [isMergeReasonDrawerOpen, setIsMergeReasonDrawerOpen] = useState(false);

  const dispatch = useFeedbackOnFeedbackStore(({ dispatch }) => dispatch);
  const recordInEditMode = useFeedbackOnFeedbackStore(
    ({ recordInEditMode }) => recordInEditMode,
  );

  const onEditToggle = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsMenuOpen((prev) => !prev);
  };

  const onMenuItemClick = (event: React.MouseEvent) => {
    if (
      !isClickable ||
      (popperRef.current &&
        popperRef.current.contains(event.target as HTMLElement))
    ) {
      // The click originated from an element inside the Popper, ignore it
      return;
    }
    onClick();
  };

  const onMouseEnter = () => setIsHovered(true);

  const onMouseLeave = useCallback(() => {
    setIsHovered(false);
    setIsMenuOpen(false);
    setIsEditCategoryMode(false);
  }, [setIsMenuOpen]);

  const {
    activeMergeSuggestion,
    setActiveMergeSuggestion,
    onUpdateSuggestedName,
    onUpdateCategory,
    onAddSuggestion,
    onRemoveSuggestion,
  } = useActiveMergeSuggestionsState();

  return (
    <>
      <Stack
        ref={anchorRef}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <MenuItem
          disableRipple
          disabled={disabled}
          onClick={onMenuItemClick}
          selected={isSelected}
          sx={{
            borderRadius: 1,
            pl: isEditable ? 2 : 1,
            pr: isEditable ? 1 : 0,
            py: 0.5,
            cursor: isClickable ? 'pointer' : 'default',
            userSelect: 'text',
            ':hover': {
              backgroundColor: isClickable ? undefined : 'transparent',
            },
          }}
        >
          <PredictionLabel
            label={label}
            isSelected={isSelected}
            isHovered={isHovered}
            size={size}
            isEditable={isEditable}
            isClickable={isClickable}
            onEditToggle={onEditToggle}
            shouldHideVisibilityIcon={shouldHideVisibilityIcon}
          />
          <Popper
            ref={popperRef}
            open={isMenuOpen}
            anchorEl={anchorRef.current}
            placement="bottom-end"
            disablePortal
            sx={{
              zIndex: 1,
              width: isEditCategoryMode ? '100%' : undefined,
            }}
          >
            <SelectablePredictionLabelMenu
              label={label}
              feedback={feedback}
              category={category}
              onClose={onMouseLeave}
              isEditCategoryMode={isEditCategoryMode}
              setIsEditCategoryMode={setIsEditCategoryMode}
              onClickEditPrediction={() => {
                setIsEditPredictionDrawerOpen(true);
                setIsMenuOpen(false);
              }}
              onClickMergeWithAnotherReason={() => {
                setIsMergeReasonDrawerOpen(true);
                setIsMenuOpen(false);
                if (label.type === FeedbackLabelType.Reason) {
                  setActiveMergeSuggestion({
                    id: label.id,
                    suggestedName: label.displayName,
                    category: label.category,
                    suggestedLabelsToMerge: [
                      {
                        id: label.id,
                        label: label.displayName,
                        category: label.category,
                      },
                    ],
                  });
                }
              }}
            />
          </Popper>
        </MenuItem>
      </Stack>
      {label.type === FeedbackLabelType.Reason && isEditPredictionDrawerOpen && (
        <EditReasonDrawer
          isOpen={isEditPredictionDrawerOpen}
          onClose={() => {
            setIsEditPredictionDrawerOpen(false);
          }}
          onEditReason={(reasonName) => {
            dispatch({
              type: FeedbackOnFeedbackActionType.SET_RECORD_IN_EDIT_MODE,
              payload: {
                id: feedback.id,
                editPredictions: [
                  ...(recordInEditMode?.editPredictions ?? []),
                  {
                    type: FeedbackLabelType.Reason,
                    manipulationType: ManipulationType.Add,
                    isSuggested: false,
                    prediction: {
                      ...label,
                      displayName: reasonName,
                    },
                  },
                ],
              },
            });
          }}
          reasonID={label.id}
          activeReasonData={{
            id: label.id,
            displayName: label.displayName,
            type: LabelType.Reason,
            isVisible: true,
            updateInProgress: false,
            metadata: {
              category: label.category,
            },
          }}
        />
      )}
      {label.type === FeedbackLabelType.Reason &&
        isMergeReasonDrawerOpen &&
        !!activeMergeSuggestion && (
          <MergeSuggestionDetailsDrawer
            activeMergeSuggestion={activeMergeSuggestion}
            closeDrawer={() => {
              setIsMergeReasonDrawerOpen(false);
              setActiveMergeSuggestion(undefined);
            }}
            title="Merge Reasons"
            onUpdateSuggestedName={onUpdateSuggestedName}
            onUpdateCategory={onUpdateCategory}
            onAddSuggestion={onAddSuggestion}
            onRemoveSuggestion={onRemoveSuggestion}
            drawerSource="Feedback Record"
          />
        )}
      {label.type === FeedbackLabelType.Entity && isEditPredictionDrawerOpen && (
        <TrackedKeywordsEditDrawer
          isOpen={isEditPredictionDrawerOpen}
          onClose={() => {
            setIsEditPredictionDrawerOpen(false);
          }}
          trackedKeywordID={label.id}
          activeTrackedKeywordData={{
            id: label.id,
            displayName: label.displayName,
            type: LabelType.Aspect,
            isVisible: true,
            updateInProgress: false,
          }}
        />
      )}
    </>
  );
};
